import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { UserPlus, Wrench, Package, Plus, X } from "lucide-react";
import { ReactSelect } from "@/components/ui/react-select";
import apiClient from "../../api/apiClient";
import { API_ENDPOINTS } from "../../api/header";
import { useSelector } from "react-redux";
import CustomerModal from "../../components/modals/CustomerModal";
import { toast } from "sonner";
import { all_routes } from "../../Router/all_routes";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { isStoreChargingTax } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PageHeader,
  PageHeaderHeading,
  PageHeaderActions,
} from "@/components/ui/page-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Spinner } from "@/components/ui/spinner";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import DefaultEditor from "react-simple-wysiwyg";

const Repairs = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const predefinedId = id || searchParams.get("predefined");
  const navigate = useNavigate();

  const [customer, setCustomer] = useState(null);
  const [customerModalOpen, setCustomerModalOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [productSearchTerm, setProductSearchTerm] = useState("");

  const [repairData, setRepairData] = useState({
    name: "",
    repair_category_ids: [],
    item_description: "",
    issue_description: "",
    diagnosis: "",
    estimated_cost: 0,
    priority: "normal",
    estimated_completion_date: "",
    customer_notes: "",
    technician_notes: "",
    serial_number: "",
    device_password: "",
    additional_notes: "",
    technician_id: "",
    store_id: "",
    parts_n_components: [],
    labor_n_timetracking: [],
  });

  const currentUser = useSelector((state) => state.user_data);
  const currentStore = useSelector((state) => state.current_store);

  const permissions = useSelector((state) => state.userpermission_data) || [];

  const hasPermission = (permission) => {
    return permissions && permissions.includes(permission);
  };
  const currency = useSelector(
    (state) => state.current_store && state.current_store.default_currency,
  );
  const currencySymbol = currency?.symbol || "";

  // ==================== QUERIES ====================

  const { data: employees = [] } = useQuery({
    queryKey: ["employees"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.USERS.EMPLOYEES);
      return res.data.data.filter((emp) => emp.status === "active");
    },
  });

  const { data: categories = [] } = useQuery({
    queryKey: ["repair-categories"],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.REPAIRS.CATEGORIES);
      return res.data.data;
    },
  });

  const { data: products = [], isFetching: productsLoading } = useQuery({
    queryKey: ["all-products", productSearchTerm],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.INVENTORY.POS_PRODUCTS, {
        params: {
          per_page: 20,
          search: productSearchTerm || undefined,
          with: ["brand", "model", "unit", "categories"],
        },
      });

      return res.data.data || [];
    },
    keepPreviousData: true,
  });

  const { data: customers = [], isFetching: customersLoading } = useQuery({
    queryKey: ["customers", customerSearchTerm],
    queryFn: async () => {
      if (!customerSearchTerm) return [];
      const res = await apiClient.get(API_ENDPOINTS.SALES.CUSTOMERS, {
        params: { q: customerSearchTerm },
      });
      return res.data.data;
    },
    enabled: customerSearchTerm.length > 2,
  });

  const { data: customerData } = useQuery({
    queryKey: ["customer", selectedCustomerId],
    queryFn: async () => {
      const res = await apiClient.get(
        API_ENDPOINTS.SALES.CUSTOMER_DETAILS(selectedCustomerId),
      );
      return res.data.data;
    },
    enabled: !!selectedCustomerId,
  });

  const { data: predefinedRepairData, isLoading: predefinedLoading } = useQuery(
    {
      queryKey: ["predefined-repair", predefinedId],
      queryFn: async () => {
        const res = await apiClient.get(
          API_ENDPOINTS.REPAIRS.PREDEFINED_JOB_DETAILS(predefinedId),
        );
        return res.data.data;
      },
      enabled: !!predefinedId,
    },
  );

  // ==================== EFFECTS ====================
  useEffect(() => {
    if (customerData) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCustomer(customerData);
    }
  }, [customerData]);

  useEffect(() => {
    if (predefinedRepairData) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRepairData((prev) => ({
        ...prev,
        name: predefinedRepairData.name || "",
        repair_category_ids: predefinedRepairData.repair_category_ids || [],
        item_description: predefinedRepairData.item_description || "",
        issue_description: predefinedRepairData.issue_description || "",
        diagnosis: predefinedRepairData.diagnosis || "",
        estimated_cost: predefinedRepairData.estimated_cost || 0,
        priority: predefinedRepairData.priority || "normal",
        estimated_completion_date:
          predefinedRepairData.estimated_completion_date || "",
        customer_notes: predefinedRepairData.customer_notes || "",
        technician_notes: predefinedRepairData.technician_notes || "",
        parts_n_components: predefinedRepairData.parts_n_components || [],
        labor_n_timetracking: predefinedRepairData.labor_n_timetracking || [],
      }));
    }
  }, [predefinedRepairData]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRepairData((prev) => ({
      ...prev,
      technician_id: currentUser?.employee_id || "",
      store_id: currentStore?.id || "",
    }));
  }, [currentUser, currentStore]);

  // ==================== MUTATIONS ====================

  const createRepairJobMutation = useMutation({
    mutationFn: async ({ jobData, parts }) => {
      const endpoint = predefinedId
        ? API_ENDPOINTS.REPAIRS.CREATE_JOB(predefinedId)
        : API_ENDPOINTS.REPAIRS.JOBS;
      const response = await apiClient.post(endpoint, jobData);
      const job = response.data.data;

      // Post parts in parallel
      const partsToPost = (parts || []).filter((p) => p.part_id);
      await Promise.all(
        partsToPost.map((part) =>
          apiClient.post(API_ENDPOINTS.REPAIRS.PARTS(job.id), {
            product_id: part.part_id,
            quantity_used: parseFloat(part.quantity) || 1,
            unit_cost: parseFloat(part.cost) || 0,
            notes: part.notes || null,
          }),
        ),
      );

      return job;
    },
    onSuccess: (data) => {
      toast.success(
        `Repair job created successfully! Job ID: ${data?.repair_number || "N/A"}`,
      );
      setTimeout(() => {
        navigate(all_routes.repairDashboard);
      }, 1500);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to create repair job",
      );
    },
  });

  // ==================== HELPERS ====================

  const handleCreateRepairJob = () => {
    if (!selectedCustomerId) {
      toast.error("Please select a customer");
      return;
    }

    // Strip template/UI-only fields - API only accepts RepairJobCreateRequest fields
    const {
      parts_n_components,
      labor_n_timetracking: _labor_n_timetracking,
      name: _name,
      additional_notes: _additional_notes,
      ...validRepairData
    } = repairData;

    const jobData = {
      ...validRepairData,
      customer_id: selectedCustomerId,
      store_id: currentStore?.id,
      technician_id: repairData.technician_id || currentUser?.employee_id,
    };

    createRepairJobMutation.mutate({ jobData, parts: parts_n_components });
  };

  const calculateTotals = () => {
    const parts = repairData.parts_n_components || [];
    const labors = repairData.labor_n_timetracking || [];

    let totalPartsCost = 0;
    let totalPartsTax = 0;

    const chargeSalesTax = isStoreChargingTax(currentStore);
    const taxRate = parseFloat(currentStore?.tax_percentage) || 0;

    parts.forEach((part) => {
      const quantity = part.quantity || 1;
      const unitPrice = parseFloat(part.cost) || 0;
      const lineTotal = unitPrice * quantity;

      const product = products.find((p) => p.id === part.part_id);
      const isTaxable = product ? product.is_taxable === 1 : true;

      if (chargeSalesTax && isTaxable) {
        const taxMethod = product?.tax_method || "exclusive";
        if (taxMethod === "inclusive") {
          const itemTax = lineTotal - lineTotal / (1 + taxRate / 100);
          totalPartsTax += itemTax;
          totalPartsCost += lineTotal - itemTax;
        } else {
          const itemTax = lineTotal * (taxRate / 100);
          totalPartsTax += itemTax;
          totalPartsCost += lineTotal;
        }
      } else {
        totalPartsCost += lineTotal;
      }
    });

    const totalLaborCost = labors.reduce(
      (sum, labor) =>
        sum + parseFloat(labor.hourly_rate || 0) * parseFloat(labor.hours || 0),
      0,
    );

    return {
      parts: totalPartsCost,
      partsTax: totalPartsTax,
      labor: totalLaborCost,
      grandTotal: totalPartsCost + totalPartsTax + totalLaborCost,
    };
  };

  const totals = calculateTotals();

  const customerNameLabel = (customer) => {
    if (!customer) return "";
    const displayName =
      customer.customer_type !== "individual" && customer.company
        ? customer.company
        : customer.full_name;
    const labels = [displayName, customer.phone].filter(Boolean);
    return labels.join(" - ");
  };

  if (predefinedLoading) {
    return (
      <div className="flex h-[400px] w-full items-center justify-center">
        <Spinner size={48} />
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content p-6 max-w-[100vw] overflow-x-hidden">
        <PageHeader>
          <PageHeaderHeading
            title={predefinedRepairData?.name || "New Repair Job"}
            subtitle="Create a new repair job"
          />
          <PageHeaderActions>
            <Button
              variant="outline"
              onClick={() => navigate(all_routes.repairDashboard)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateRepairJob}
              disabled={createRepairJobMutation.isPending}
            >
              {createRepairJobMutation.isPending
                ? "Creating..."
                : "Create Repair Job"}
            </Button>
          </PageHeaderActions>
        </PageHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 3xl:grid-cols-2 gap-6">
          {/* Left Column - Customer & Summary */}
          <div className="space-y-6">
            {/* Customer Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Customer</span>
                  {hasPermission("customer create") && (
                    <Button
                      size="sm"
                      onClick={() => setCustomerModalOpen(true)}
                    >
                      <UserPlus className="h-4 w-4 mr-2" />
                      Add New
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Search Customer</Label>
                  <ReactSelect
                    isLoading={customersLoading}
                    filterOption={() => true}
                    options={customers.map((customer) => ({
                      value: customer.id,
                      label: customerNameLabel(customer),
                    }))}
                    placeholder="Search Customer"
                    onInputChange={(inputValue) => {
                      if (inputValue.length > 2) {
                        setCustomerSearchTerm(inputValue);
                      }
                      return inputValue;
                    }}
                    onChange={(e) => {
                      if (e && e.value) {
                        setSelectedCustomerId(e.value);
                      }
                    }}
                  />
                </div>

                {customer && (
                  <div className="border rounded-lg p-4 bg-muted/50">
                    <h6 className="font-semibold mb-2">
                      {customer.customer_type !== "individual" &&
                        customer.company
                        ? customer.company
                        : customer.full_name}
                    </h6>
                    {customer.email && (
                      <p className="text-sm text-muted-foreground">
                        {customer.email}
                      </p>
                    )}
                    {customer.phone && (
                      <p className="text-sm text-muted-foreground">
                        {customer.phone}
                      </p>
                    )}
                    {customer.street_address && (
                      <p className="text-sm text-muted-foreground mt-2">
                        {customer.house_number && `${customer.house_number}, `}
                        {customer.street_address}
                        {customer.city && `, ${customer.city}`}
                        {customer.postcode && ` ${customer.postcode}`}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cost Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Cost Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Parts Cost:</span>
                    <span className="font-medium">
                      {currencySymbol}
                      {totals.parts.toFixed(2)}
                    </span>
                  </div>
                  {totals.partsTax > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Tax:</span>
                      <span className="font-medium">
                        {currencySymbol}
                        {totals.partsTax.toFixed(2)}
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Labor Cost:</span>
                    <span className="font-medium">
                      {currencySymbol}
                      {totals.labor.toFixed(2)}
                    </span>
                  </div>
                  <div className="border-t pt-2 flex justify-between">
                    <span className="font-semibold">Total:</span>
                    <span className="font-semibold text-lg">
                      {currencySymbol}
                      {totals.grandTotal.toFixed(2)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Repair Details */}
          <div className="lg:col-span-2 3xl:col-span-1">
            <Card>
              <CardContent className="pt-6">
                <Tabs defaultValue="general" className="w-full">
                  <TabsList className="grid w-full grid-cols-4">
                    <TabsTrigger value="general">General</TabsTrigger>
                    <TabsTrigger value="details">Details</TabsTrigger>
                    <TabsTrigger value="parts">Parts</TabsTrigger>
                    <TabsTrigger value="labor">Labor</TabsTrigger>
                  </TabsList>

                  <div className="py-4">
                    <TabsContent value="general" className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Repair Category</Label>
                          <ReactSelect
                            isMulti
                            options={categories.map((cat) => ({
                              value: cat.id,
                              label: cat.name,
                            }))}
                            value={categories
                              .filter((cat) =>
                                repairData.repair_category_ids.includes(cat.id),
                              )
                              .map((cat) => ({
                                value: cat.id,
                                label: cat.name,
                              }))}
                            onChange={(selectedOptions) => {
                              setRepairData({
                                ...repairData,
                                repair_category_ids: selectedOptions
                                  ? selectedOptions.map((opt) => opt.value)
                                  : [],
                              });
                            }}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Priority</Label>
                          <Select
                            value={repairData.priority}
                            onValueChange={(value) =>
                              setRepairData({ ...repairData, priority: value })
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">Low</SelectItem>
                              <SelectItem value="normal">Normal</SelectItem>
                              <SelectItem value="high">High</SelectItem>
                              <SelectItem value="urgent">Urgent</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Technician</Label>
                          <ReactSelect
                            options={employees.map((emp) => ({
                              value: emp.id,
                              label: emp.full_name,
                            }))}
                            value={repairData.technician_id}
                            onChange={(option) =>
                              setRepairData({
                                ...repairData,
                                technician_id: option?.value || "",
                              })
                            }
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Estimated Completion Date</Label>
                          <Input
                            type="datetime-local"
                            value={repairData.estimated_completion_date}
                            onChange={(e) =>
                              setRepairData({
                                ...repairData,
                                estimated_completion_date: e.target.value,
                              })
                            }
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Serial Number</Label>
                          <Input
                            value={repairData.serial_number}
                            onChange={(e) =>
                              setRepairData({
                                ...repairData,
                                serial_number: e.target.value,
                              })
                            }
                          />
                        </div>

                        {hasPermission("repair job view_device_password") && (
                          <div className="space-y-2">
                            <Label>Device Password</Label>
                            <Input
                              type="password"
                              value={repairData.device_password}
                              onChange={(e) =>
                                setRepairData({
                                  ...repairData,
                                  device_password: e.target.value,
                                })
                              }
                            />
                          </div>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="details" className="space-y-4">
                      <div className="space-y-2">
                        <Label>Item Description</Label>
                        <Input
                          value={repairData.item_description}
                          onChange={(e) =>
                            setRepairData({
                              ...repairData,
                              item_description: e.target.value,
                            })
                          }
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Issue Description</Label>
                        <DefaultEditor
                          value={repairData.issue_description}
                          onChange={(e) =>
                            setRepairData({
                              ...repairData,
                              issue_description: e.target.value,
                            })
                          }
                          className="min-h-[140px] rounded-md border-border overflow-hidden"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Diagnosis</Label>
                        <DefaultEditor
                          value={repairData.diagnosis}
                          onChange={(e) =>
                            setRepairData({
                              ...repairData,
                              diagnosis: e.target.value,
                            })
                          }
                          className="min-h-[140px] rounded-md border-border overflow-hidden"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Customer Notes</Label>
                          <DefaultEditor
                            value={repairData.customer_notes}
                            onChange={(e) =>
                              setRepairData({
                                ...repairData,
                                customer_notes: e.target.value,
                              })
                            }
                            className="min-h-[180px] rounded-md border-border overflow-hidden"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Technician Notes</Label>
                          <DefaultEditor
                            value={repairData.technician_notes}
                            onChange={(e) =>
                              setRepairData({
                                ...repairData,
                                technician_notes: e.target.value,
                              })
                            }
                            className="min-h-[180px] rounded-md border-border overflow-hidden"
                          />
                        </div>
                      </div>
                    </TabsContent>

                    <TabsContent value="parts" className="space-y-4">
                      <div className="flex justify-between items-center">
                        <Label className="text-base font-semibold">
                          Parts & Components
                        </Label>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setRepairData({
                              ...repairData,
                              parts_n_components: [
                                ...repairData.parts_n_components,
                                {
                                  part_id: "",
                                  product_name: "",
                                  tracking_type: "none",
                                  quantity: 1,
                                  cost: 0,
                                  notes: "",
                                },
                              ],
                            });
                          }}
                        >
                          <Plus className="mr-2 h-4 w-4" /> Add Part
                        </Button>
                      </div>

                      {repairData.parts_n_components.map((part, index) => (
                        <div
                          key={index}
                          className="border p-4 rounded-md space-y-3 relative"
                        >
                          <Button
                            variant="ghost"
                            size="icon"
                            className="absolute right-2 top-2 text-destructive"
                            onClick={() => {
                              const updatedParts =
                                repairData.parts_n_components.filter(
                                  (_, i) => i !== index,
                                );
                              setRepairData({
                                ...repairData,
                                parts_n_components: updatedParts,
                              });
                            }}
                          >
                            <X className="h-4 w-4" />
                          </Button>

                          <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-2">
                              <Label>Part</Label>
                              <ReactSelect
                                placeholder="Search part..."
                                onInputChange={(val) =>
                                  setProductSearchTerm(val)
                                }
                                options={products.map((p) => ({
                                  value: p.id,
                                  label: `${p.name}${p.brand ? ` (${p.brand.name})` : ""}${p.model ? ` - ${p.model.name}` : ""}${p.sku ? ` [${p.sku}]` : ""}`,
                                }))}
                                isLoading={productsLoading}
                                value={
                                  part.part_id
                                    ? {
                                      value: part.part_id,
                                      label:
                                        part.product_name ||
                                        part.product?.name ||
                                        products.find(
                                          (p) => p.id === part.part_id,
                                        )?.name ||
                                        "Select part",
                                    }
                                    : null
                                }
                                onChange={(option) => {
                                  const updatedParts = [
                                    ...repairData.parts_n_components,
                                  ];
                                  const product = products.find(
                                    (p) => p.id === option?.value,
                                  );
                                  updatedParts[index].part_id =
                                    option?.value || "";
                                  if (product) {
                                    updatedParts[index].cost =
                                      parseFloat(product.sell_price) || 0;
                                    updatedParts[index].product_name =
                                      product.name || "";
                                    updatedParts[index].tracking_type =
                                      product.tracking_type ||
                                      (product.is_serialized
                                        ? "serial"
                                        : "none");
                                  }
                                  setRepairData({
                                    ...repairData,
                                    parts_n_components: updatedParts,
                                  });
                                }}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Quantity</Label>
                              <Input
                                type="number"
                                value={part.quantity}
                                onChange={(e) => {
                                  const updatedParts = [
                                    ...repairData.parts_n_components,
                                  ];
                                  updatedParts[index].quantity =
                                    parseInt(e.target.value) || 1;
                                  setRepairData({
                                    ...repairData,
                                    parts_n_components: updatedParts,
                                  });
                                }}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Cost</Label>
                              <Input
                                type="number"
                                step="0.01"
                                value={part.cost}
                                onChange={(e) => {
                                  const updatedParts = [
                                    ...repairData.parts_n_components,
                                  ];
                                  updatedParts[index].cost =
                                    parseFloat(e.target.value) || 0;
                                  setRepairData({
                                    ...repairData,
                                    parts_n_components: updatedParts,
                                  });
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </TabsContent>

                    <TabsContent value="labor" className="space-y-4">
                      <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground space-y-1">
                        <p className="font-medium text-foreground">
                          Labor tracking
                        </p>
                        <p>
                          Labor entries require start/end times and are tracked
                          after the repair job is created. Use the{" "}
                          <strong>Repair Dashboard → Add Labor</strong> action
                          on the created job to log technician time.
                        </p>
                      </div>
                    </TabsContent>
                  </div>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Add Customer Modal */}
        <CustomerModal
          open={customerModalOpen}
          onOpenChange={setCustomerModalOpen}
          onSuccess={(createdCustomer) => {
            setSelectedCustomerId(createdCustomer.id);
          }}
        />
      </div>
    </div>
  );
};

export default Repairs;
